import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
	Alert,
	AlertTitle,
	Backdrop,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Stack,
	Typography,
} from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';

import routes from '../../../../../constants/routes';
import { getSaleOrderIdFromNetsuiteId } from '../../../../../features/salesOrder/services';
import { extractErrorMessage } from '../../../../../features/quotation/helpers';

type RouteParams = {
	externalId?: string; // viene desde /salesorder-from-external/:externalId
};

export default function SalesOrderFromExternalRedirect(): JSX.Element {
	const { externalId } = useParams<RouteParams>();
	const location = useLocation();
	const history = useHistory();

	const [loading, setLoading] = useState<boolean>(false);
	const [notFound, setNotFound] = useState<boolean>(false);
	const [genericError, setGenericError] = useState<string | null>(null);
	const [invalidId, setInvalidId] = useState<boolean>(false);

	// Tomamos serviceId del query string si viene
	const serviceId = useMemo(() => {
		const params = new URLSearchParams(location.search);
		const s = params.get('serviceId');
		return s ?? undefined;
	}, [location.search]);

	// Valida que el id sea un entero positivo (solo dígitos)
	const parseExternalId = useCallback((): number | null => {
		if (!externalId) return null;
		if (!/^\d+$/.test(externalId)) return null; // no numérico
		const n = Number(externalId);
		if (!Number.isFinite(n)) return null;
		return n;
	}, [externalId]);

	const buildTargetUrl = useCallback(
		(internalId: string) => {
			const base = routes.salesOrder.edit.replace(':id', internalId);
			if (serviceId) {
				return `${base}?serviceId=${encodeURIComponent(serviceId)}`;
			}
			return base;
		},
		[serviceId],
	);

	const fetchAndRedirect = useCallback(async () => {
		setLoading(true);
		setNotFound(false);
		setGenericError(null);

		try {
			const parsed = parseExternalId();
			if (parsed === null) {
				setInvalidId(true);
				setLoading(false);
				return;
			}

			setInvalidId(false);

			const resp = await getSaleOrderIdFromNetsuiteId(parsed);
			const internalId = resp.data?.id;

			if (internalId) {
				const url = buildTargetUrl(internalId);
				history.replace(url); // v5: reemplaza la URL actual
			} else {
				setNotFound(true);
			}
		} catch (error: any) {
			const status = error?.response?.status;
			if (status === 404) {
				setNotFound(true);
			} else {
				setGenericError(extractErrorMessage(error, 'Ocurrió un error al buscar la Sale Order.'));
			}
		} finally {
			setLoading(false);
		}
	}, [buildTargetUrl, history, parseExternalId]);

	useEffect(() => {
		fetchAndRedirect();
	}, [fetchAndRedirect]);

	if (loading) {
		return (
			<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
				<CircularProgress color="inherit" />
			</Backdrop>
		);
	}

	if (invalidId) {
		return (
			<Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh" px={2}>
				<Alert severity="error" icon={<ErrorOutlineIcon />}>
					<AlertTitle>ID inválido</AlertTitle>
					El identificador de la URL <strong>debe ser numérico</strong>. Recibido:{' '}
					<code>{externalId ?? '(vacío)'}</code>.
				</Alert>
			</Box>
		);
	}

	if (notFound) {
		return (
			<Box display="flex" alignItems="center" justifyContent="center" minHeight="70vh" px={2}>
				<Card sx={{ maxWidth: 560, width: '100%', textAlign: 'center', borderRadius: 4, boxShadow: 6 }}>
					<CardContent>
						<Stack spacing={2} alignItems="center">
							<SearchOffIcon sx={{ fontSize: 64, opacity: 0.8 }} />
							<Typography variant="h6" fontWeight={700}>
								No encontramos una Sale Order para ese ID
							</Typography>
							<Typography variant="body1" color="text.secondary">
								Revisá el ID externo (<strong>{externalId}</strong>) e intentá nuevamente.
							</Typography>
						</Stack>
					</CardContent>
					<CardActions sx={{ justifyContent: 'center', pb: 3 }}>
						<Button startIcon={<RefreshIcon />} variant="contained" onClick={fetchAndRedirect}>
							Reintentar
						</Button>
						<Button startIcon={<HomeIcon />} variant="text" onClick={() => history.push('/')}>
							Ir al inicio
						</Button>
					</CardActions>
				</Card>
			</Box>
		);
	}

	if (genericError) {
		return (
			<Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh" px={2}>
				<Stack spacing={2} maxWidth={720} width="100%">
					<Alert severity="error" icon={<ErrorOutlineIcon />}>
						<AlertTitle>Error</AlertTitle>
						Hubo un problema al buscar la Sale Order. <br />
						<Typography variant="body2" mt={1}>
							Detalle: <code>{genericError}</code>
						</Typography>
					</Alert>
					<Stack direction="row" spacing={2}>
						<Button startIcon={<RefreshIcon />} variant="contained" onClick={fetchAndRedirect}>
							Reintentar
						</Button>
						<Button startIcon={<HomeIcon />} variant="outlined" onClick={() => history.push('/')}>
							Ir al inicio
						</Button>
					</Stack>
				</Stack>
			</Box>
		);
	}

	return <></>;
}
